import React, {useState, useCallback, useEffect} from 'react'
import XLSX from 'xlsx'
import {makeStyles, withStyles} from '@material-ui/core/styles'
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    IconButton,
} from '@material-ui/core'
import CircularProgress from '@material-ui/core/CircularProgress'
import Close from '@material-ui/icons/Close'
import BatchUploadFormatGuide from './BatchUploadFormatGuide'
import BatchUploadFileSearchView from './BatchUploadFileSearchView'

// using hook API
const useStyles = makeStyles(theme => ({
    boundaryBox: {
        display: 'flex',
        flexDirection: 'column',
        margin: '15px 0',
        fontSize: '0.825rem',
    },
    spinnerBox: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        padding: '0 0 40px 0',
    },
    submitButton: {
        color: theme.palette.common.white,
        backgroundColor: theme.palette.primary.main,
        margin: theme.spacing(1, 0),
        boxShadow: 'none',
        borderRadius: theme.shape.borderRadius,
        height: '2.625rem',
        textTransform: 'uppercase',
        width: '100%',
        '&:hover': {
            boxShadow: 'none',
            backgroundColor: '#00652A',
        },
    },
    rotateIcon: {
        margin: '-15px 5px 0 0',
        height: '140px',
    },
    container: {
        display: 'flex',
        flexDirection: 'row',
        border: '2px dashed #DBDBDB',
        padding: '10px',
    },
    fileList: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        wordBreak: 'break-word',
    },
    removeButton: {
        padding: '0 0 10px 5px',
    },
}))

const GreenCheckbox = withStyles({
    root: {
        color: '#098447',
        '&$checked': {
            color: '#098447',
        },
    },
    checked: {},
})(props => <Checkbox color="default" {...props} />)

const BatchUploadFileSearch = props => {
    const classes = useStyles()

    const {
        fetchingSwipeValidation,
        removeDuplicates,
        setRemoveDuplicates,
        setbatchSwipeArray,
        callbackfunction,
        detectChange,
        setMissingHeaderFile,
    } = props

    const [myFiles, setMyFiles] = useState([])
    const [rejectedFiles, setRejectedFiles] = useState([])
    const batchSwipeArray = []

    const headerOkFiles = []
    let missingHeaderFiles

    const checkFileHeaders = files => {
        missingHeaderFiles = []
        files.forEach(file => {
            const reader = new FileReader()
            const rABS = !!reader.readAsBinaryString

            reader.onload = e => {
                /* Parse data */
                const bstr = e.target.result
                const wb = XLSX.read(bstr, {type: rABS ? 'binary' : 'array'})
                /* Get first worksheet */
                const wsname = wb.SheetNames[0]
                const ws = wb.Sheets[wsname]
                /* Convert array of arrays */
                const data = XLSX.utils.sheet_to_json(ws)
                /* Update state */
                if ('ID' in data[0]) {
                    headerOkFiles.push(file)
                } else {
                    missingHeaderFiles.push(file)
                }
            }
            if (rABS) reader.readAsBinaryString(file)
            else reader.readAsArrayBuffer(file)
        })
    }

    const getBatchSwipeIDs = files => {
        files.forEach(file => {
            const reader = new FileReader()
            const rABS = !!reader.readAsBinaryString
            reader.onload = e => {
                /* Parse data */
                const bstr = e.target.result
                const wb = XLSX.read(bstr, {type: rABS ? 'binary' : 'array'})
                /* Get first worksheet */
                const wsname = wb.SheetNames[0]
                const ws = wb.Sheets[wsname]
                /* Convert array of arrays */
                const data = XLSX.utils.sheet_to_json(ws)
                /* Update state */
                if ('ID' in data[0]) {
                    data.forEach(element => {
                        let euid = String(element.ID).trim()
                        batchSwipeArray.push(euid)
                    })
                }
            }

            if (rABS) reader.readAsBinaryString(file)
            else reader.readAsArrayBuffer(file)
        })
    }

    const onDropAccepted = useCallback(
        acceptedFiles => {
            setMyFiles([...myFiles, ...acceptedFiles])
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [myFiles]
    )

    const onDropRejected = useCallback(
        fileRejections => {
            setRejectedFiles([...rejectedFiles, ...fileRejections])
        },
        [rejectedFiles]
    )

    const removeFile = file => () => {
        const newFiles = [...myFiles]
        newFiles.splice(newFiles.indexOf(file), 1)
        setMyFiles(newFiles)
    }

    const removeRejectedFile = file => () => {
        const newFiles = [...rejectedFiles]
        newFiles.splice(newFiles.indexOf(file), 1)
        setRejectedFiles(newFiles)
    }

    const files = myFiles.map(file => (
        <li key={file.path}>
            <Box className={classes.fileList}>
                {file.path} - {file.size} bytes{' '}
                <IconButton
                    onClick={removeFile(file)}
                    className={classes.removeButton}
                >
                    <Close style={{color: '#000000'}} />
                </IconButton>
            </Box>
        </li>
    ))

    const rejectedfileItems = rejectedFiles.map(file => (
        <li key={'rejected-' + file.file.path}>
            <Box className={classes.fileList}>
                {file.file.path} - {file.errors[0].code}{' '}
                <IconButton
                    onClick={removeRejectedFile(file)}
                    className={classes.removeButton}
                >
                    <Close style={{color: '#000000'}} />
                </IconButton>
            </Box>
        </li>
    ))

    const displayFilename = files.length > 0 ? true : false
    const displayRejectedFilename = rejectedfileItems.length > 0 ? true : false

    if (displayFilename || displayRejectedFilename) {
        detectChange.current = true
    } else {
        detectChange.current = false
    }

    const handleProcessSwipe = () => {
        //setActiveStep(2)
        callbackfunction('Batch Upload Review Results')
    }

    useEffect(
        () => {
            setMissingHeaderFile(missingHeaderFiles)
            getBatchSwipeIDs(myFiles)
            setbatchSwipeArray(batchSwipeArray)
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [myFiles]
    )

    checkFileHeaders(myFiles)

    return (
        <>
            {fetchingSwipeValidation ? (
                <Box className={classes.spinnerBox}>
                    <CircularProgress
                        style={{
                            color: '#00853e',
                        }}
                        thickness={3}
                    />
                </Box>
            ) : (
                <>
                    <BatchUploadFormatGuide />
                    <BatchUploadFileSearchView
                        myFiles={myFiles}
                        setMyFiles={setMyFiles}
                        rejectedFiles={rejectedFiles}
                        setRejectedFiles={setRejectedFiles}
                        onDropAccepted={onDropAccepted}
                        onDropRejected={onDropRejected}
                        detectChange={detectChange}
                        missingHeaderFiles={missingHeaderFiles}
                    />
                    <Box className={classes.boundaryBox}>
                        {displayFilename && (
                            <FormControlLabel
                                control={
                                    <GreenCheckbox
                                        checked={removeDuplicates}
                                        onChange={() =>
                                            setRemoveDuplicates(
                                                !removeDuplicates
                                            )
                                        }
                                        name="removeduplicates"
                                    />
                                }
                                label="Remove Duplicate Records"
                            />
                        )}

                        <Button
                            variant="contained"
                            size="medium"
                            disabled={
                                !displayFilename || displayRejectedFilename
                            }
                            className={classes.submitButton}
                            onClick={() => handleProcessSwipe()}
                        >
                            Process Swipes
                        </Button>
                    </Box>
                </>
            )}
        </>
    )
}

export default BatchUploadFileSearch
